import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface Profile {
  id: string;
  name: string;
  age: number;
  user_id?: string;
  country_id?: string;
  region_id?: string;
}

export function useProfiles() {
  const navigate = useNavigate();
  const location = useLocation();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [isAddingProfile, setIsAddingProfile] = useState(false);
  const [newName, setNewName] = useState('');
  const [newAge, setNewAge] = useState('');
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
      setLoading(false);
      
      if (data.session) {
        console.log("User is logged in, fetching profiles");
        fetchProfiles(data.session.user.id);
      } else {
        console.log("No user session, setting default profile");
        setProfiles([{ id: '1', name: 'Usuario Principal', age: 35 }]);
      }
      
      if (location.hash && location.hash.includes('access_token')) {
        navigate('/profile', { replace: true });
        toast.success('Inicio de sesión exitoso');
      }
      
      const { data: authListener } = supabase.auth.onAuthStateChange(
        (event, newSession) => {
          console.log("Auth state changed:", event);
          setSession(newSession);
          if (event === 'SIGNED_IN') {
            console.log("User signed in:", newSession?.user.id);
            toast.success('Inicio de sesión exitoso');
            if (newSession) {
              fetchProfiles(newSession.user.id);
            }
            if (location.hash) {
              navigate('/profile', { replace: true });
            }
          }
          if (event === 'SIGNED_OUT') {
            toast.success('Sesión cerrada');
            setProfiles([{ id: '1', name: 'Usuario Principal', age: 35 }]);
          }
        }
      );
      
      return () => {
        authListener.subscription.unsubscribe();
      };
    };
    
    checkSession();
  }, [location, navigate]);
  
  const fetchProfiles = async (userId: string) => {
    if (!userId) {
      console.error("No user ID provided to fetchProfiles");
      return;
    }
    
    try {
      setLoading(true);
      console.log("Fetching profiles for user:", userId);
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId);
        
      if (error) {
        console.error("Error fetching profiles:", error);
        toast.error('Error al cargar perfiles: ' + error.message);
        return;
      }
      
      console.log("Profiles fetched:", data);
      
      if (data && data.length > 0) {
        setProfiles(data);
      } else {
        console.log("No profiles found, creating default profile");
        const userName = session?.user.user_metadata?.full_name || 
                         session?.user.user_metadata?.name || 
                         'Usuario Principal';
        
        const { data: newProfile, error: insertError } = await supabase
          .from('profiles')
          .insert({
            name: userName,
            age: 35,
            user_id: userId
          })
          .select();
          
        if (insertError) {
          console.error("Error creating default profile:", insertError);
          toast.error('Error al crear perfil predeterminado: ' + insertError.message);
          return;
        }
        
        console.log("Default profile created:", newProfile);
        if (newProfile) {
          setProfiles(newProfile);
        }
      }
    } catch (error: any) {
      console.error("Unexpected error in fetchProfiles:", error);
      toast.error('Error al cargar perfiles: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        throw error;
      }
      setProfiles([{ id: '1', name: 'Usuario Principal', age: 35 }]);
    } catch (error: any) {
      toast.error('Error al cerrar sesión: ' + error.message);
    }
  };
  
  const handleAddProfile = async () => {
    if (!newName.trim()) {
      toast.error("Por favor ingrese un nombre");
      return;
    }
    
    const age = parseInt(newAge);
    if (isNaN(age) || age <= 0 || age > 120) {
      toast.error("Por favor ingrese una edad válida");
      return;
    }
    
    if (!session) {
      toast.error("Debe iniciar sesión para añadir perfiles");
      return;
    }
    
    try {
      console.log("Adding profile for user:", session.user.id);
      
      const { data, error } = await supabase
        .from('profiles')
        .insert({
          name: newName.trim(),
          age: age,
          user_id: session.user.id
        })
        .select();
          
      if (error) {
        console.error("Error inserting profile:", error);
        toast.error('Error al guardar el perfil: ' + error.message);
        return;
      }
      
      console.log("Profile added successfully:", data);
      
      if (data && data.length > 0) {
        setProfiles([...profiles, ...data]);
        toast.success("Perfil añadido correctamente");
      } else {
        fetchProfiles(session.user.id);
        toast.success("Perfil añadido correctamente");
      }
      
      setNewName('');
      setNewAge('');
      setIsAddingProfile(false);
    } catch (error: any) {
      console.error("Full error details:", error);
      toast.error('Error al guardar el perfil: ' + error.message);
    }
  };
  
  const handleDeleteProfile = async (id: string) => {
    if (session) {
      try {
        console.log("Deleting profile:", id);
        
        const { error } = await supabase
          .from('profiles')
          .delete()
          .eq('id', id)
          .eq('user_id', session.user.id);
            
        if (error) {
          console.error("Error deleting profile:", error);
          toast.error('Error al eliminar el perfil: ' + error.message);
          return;
        }
        
        setProfiles(profiles.filter(profile => profile.id !== id));
        toast.success("Perfil eliminado");
      } catch (error: any) {
        console.error("Full error details:", error);
        toast.error('Error al eliminar el perfil: ' + error.message);
      }
    } else {
      setProfiles(profiles.filter(profile => profile.id !== id));
      toast.success("Perfil eliminado");
    }
  };

  return {
    profiles,
    isAddingProfile,
    setIsAddingProfile,
    newName,
    setNewName,
    newAge,
    setNewAge,
    session,
    loading,
    handleLogout,
    handleAddProfile,
    handleDeleteProfile,
    fetchProfiles
  };
}
